/**
 * SF-5126: the company's help desk forwarding address. The company forwards its own support
 * mailbox to it, and only emails that arrive there become tickets.
 *
 *   POST /admin/v1/companies/:id/help-desk/forwarding-address
 *        -> { help_desk_forwarding_address }   the old address stops working at once
 *        409 HELP_DESK_FORWARDING_OFF while ticket emails are not on forwarding
 *
 * The mode itself (`help_desk_email_mode`) is a normal one-field company PATCH. Same pattern as
 * demoDataClient.ts: the data provider unwraps the sf-api `{ success, data }` envelope.
 */
import { useCustomMutation } from '@refinedev/core';
import type { Company } from '../list';
import { COMPANIES_API_BASE } from './companyDeleteClient';

export const NO_ADDRESS_RETURNED_TEXT =
  'The new address did not come back. Refresh the page to see it.';

export function newForwardingAddressArgs(companyId: string) {
  return {
    url: `/${COMPANIES_API_BASE}/${encodeURIComponent(companyId)}/help-desk/forwarding-address`,
    method: 'post' as const,
    values: {},
    errorNotification: false as const,
    successNotification: false as const,
  };
}

/**
 * Makes a new forwarding address for a company and resolves with the company field it changed,
 * for the page's saver (`send`) to write into that company's cache.
 */
export function useNewForwardingAddress() {
  const { mutateAsync } = useCustomMutation<{ help_desk_forwarding_address?: unknown }>();
  return async (companyId: string): Promise<Partial<Company>> => {
    const { data } = await mutateAsync(newForwardingAddressArgs(companyId));
    const address = data?.help_desk_forwarding_address;
    if (typeof address !== 'string' || !address) throw new Error(NO_ADDRESS_RETURNED_TEXT);
    return { help_desk_forwarding_address: address };
  };
}
